import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AppLayout from "../../components/AppLayout";
import API_BASE_URL from "../../config/api";
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Avatar,
  IconButton,
  Tabs,
  Tab,
  Menu,
  MenuItem,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import HistoryIcon from "@mui/icons-material/History";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { getInitials, getAvatarColor } from "../../utils/avatarUtils";

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function SupplierPaymentHistory() {
  const navigate = useNavigate();
  const { supplierId } = useParams();

  const [supplier, setSupplier] = useState(null);
  const [payments, setPayments] = useState([]);
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [tabValue, setTabValue] = useState(0);
  
  // Payment dialog states
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  
  // Opening balance dialog states
  const [openBalanceDialog, setOpenBalanceDialog] = useState(false);
  
  // Menu states for actions
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  
  // Purchase details dialog states
  const [purchaseDetailsOpen, setPurchaseDetailsOpen] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  
  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    paymentMethod: "Bank Transfer",
    paymentStatus: "Completed",
    paymentDate: new Date().toISOString().split("T")[0],
    notes: "",
  });

  const [balanceForm, setBalanceForm] = useState({
    amount: "",
    effectiveDate: new Date().toISOString().split("T")[0],
    notes: "",
  });

  useEffect(() => {
    fetchSupplierData();
    fetchPaymentHistory();
    fetchPurchaseHistory();
    
    // Check if opening balance dialog should be opened
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('openBalance') === 'true') {
      setOpenBalanceDialog(true);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [supplierId]);

  const fetchSupplierData = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/suppliers/${supplierId}/balance`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setSupplier(data.data.supplier);
        setCurrentBalance(data.data.currentBalance);
        setOpeningBalance(data.data.openingBalance);
      } else {
        toast.error("Failed to load supplier details");
        navigate("/admin/suppliers");
      }
    } catch (error) {
      console.error("Error fetching supplier:", error);
      toast.error("Failed to load supplier details");
    }
  };

  const fetchPaymentHistory = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/suppliers/${supplierId}/payments`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setPayments(data.data.payments || []);
      } else {
        toast.error("Failed to load payment history");
      }
    } catch (error) {
      console.error("Error fetching payment history:", error);
      toast.error("Failed to load payment history");
    }
  };

  const fetchPurchaseHistory = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/suppliers/${supplierId}/purchase-history`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setPurchaseHistory(data.data.purchaseHistory || []);
      } else {
        toast.error("Failed to load purchase history");
      }
    } catch (error) {
      console.error("Error fetching purchase history:", error);
      toast.error("Failed to load purchase history");
    }
  };

  const handleAddPayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error("Please enter a valid payment amount");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      
      if (editingPayment) {
        // Update existing payment
        const response = await fetch(`${API_BASE_URL}/api/suppliers/${supplierId}/payments/${editingPayment.PaymentId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            amount: parseFloat(paymentForm.amount),
            paymentMethod: paymentForm.paymentMethod,
            paymentStatus: paymentForm.paymentStatus,
            paymentDate: paymentForm.paymentDate,
            notes: paymentForm.notes,
          }),
        });

        if (response.ok) {
          toast.success("Payment updated successfully");
        } else {
          toast.error("Failed to update payment");
          return;
        }
      } else {
        // Create new payment
        const response = await fetch(`${API_BASE_URL}/api/suppliers/${supplierId}/payments`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            amount: parseFloat(paymentForm.amount),
            paymentMethod: paymentForm.paymentMethod,
            paymentStatus: paymentForm.paymentStatus,
            paymentDate: paymentForm.paymentDate,
            notes: paymentForm.notes,
          }),
        });

        if (response.ok) {
          toast.success("Payment recorded successfully");
        } else {
          toast.error("Failed to record payment");
          return;
        }
      }

      setOpenPaymentDialog(false);
      setEditingPayment(null);
      setPaymentForm({
        amount: "",
        paymentMethod: "Bank Transfer",
        paymentStatus: "Completed",
        paymentDate: new Date().toISOString().split("T")[0],
        notes: "",
      });
      fetchPaymentHistory();
      fetchSupplierData();
    } catch (error) {
      console.error("Error saving payment:", error);
      toast.error("Failed to save payment");
    }
  };

  const handleEditPayment = (payment) => {
    setEditingPayment(payment);
    setPaymentForm({
      amount: Math.abs(payment.Amount),
      paymentMethod: payment.PaymentMethod || "Bank Transfer",
      paymentStatus: payment.PaymentStatus || "Completed",
      paymentDate: payment.PaymentDate.split("T")[0],
      notes: payment.Notes || "",
    });
    setOpenPaymentDialog(true);
    setAnchorEl(null);
  };

  const handleDeleteClick = (payment) => {
    setPaymentToDelete(payment);
    setDeleteDialogOpen(true);
    setAnchorEl(null);
  };

  const handleMenuClick = (event, payment) => {
    setAnchorEl(event.currentTarget);
    setSelectedPayment(payment);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPayment(null);
  };

  const handleViewPurchaseDetails = (purchase) => {
    setSelectedPurchase(purchase);
    setPurchaseDetailsOpen(true);
  };

  const handleClosePurchaseDetails = () => {
    setPurchaseDetailsOpen(false);
    setSelectedPurchase(null);
  };

  const handleDeleteConfirm = async () => {
    if (!paymentToDelete) return;

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/suppliers/${supplierId}/payments/${paymentToDelete.PaymentId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success("Payment deleted successfully");
        fetchPaymentHistory();
        fetchSupplierData();
      } else {
        toast.error("Failed to delete payment");
      }
    } catch (error) {
      console.error("Error deleting payment:", error);
      toast.error("Failed to delete payment");
    } finally {
      setDeleteDialogOpen(false);
      setPaymentToDelete(null);
    }
  };

  const handleOpeningBalance = async () => {
    if (!balanceForm.amount || parseFloat(balanceForm.amount) < 0) {
      toast.error("Please enter a valid balance amount");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const method = openingBalance > 0 ? "PUT" : "POST";
      
      const response = await fetch(`${API_BASE_URL}/api/suppliers/${supplierId}/opening-balance`, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          amount: parseFloat(balanceForm.amount),
          effectiveDate: balanceForm.effectiveDate,
          notes: balanceForm.notes,
        }),
      });

      if (response.ok) {
        toast.success(`Opening balance ${openingBalance > 0 ? 'updated' : 'set'} successfully`);
        setOpenBalanceDialog(false);
        setBalanceForm({
          amount: "",
          effectiveDate: new Date().toISOString().split("T")[0],
          notes: "",
        });
        fetchSupplierData();
      } else {
        toast.error("Failed to set opening balance");
      }
    } catch (error) {
      console.error("Error setting opening balance:", error);
      toast.error("Failed to set opening balance");
    }
  };

  if (!supplier) {
    return (
      <AppLayout>
        <Box sx={{ 
          p: 4, 
          textAlign: "center",
          bgcolor: "#0a0a0a",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}>
          <Typography sx={{ color: "#C59A58", fontSize: "1.2rem" }}>
            Loading...
          </Typography>
        </Box>
      </AppLayout>
    );
  }

  const initials = getInitials(supplier.SupplierCode);
  const avatarColor = getAvatarColor(supplier.SupplierCode);
  const isOwed = currentBalance > 0;

  return (
    <AppLayout>
      <Box sx={{ 
        maxWidth: "1400px", 
        mx: "auto", 
        p: { xs: 1, sm: 2, md: 3 },
        bgcolor: "#0a0a0a",
        minHeight: "100vh"
      }}>
        {/* HEADER */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2, flexWrap: "wrap", gap: 1.5 }}>
          <Typography variant="h3" sx={{ 
            fontWeight: 800, 
            letterSpacing: { xs: 1, sm: 2 },
            color: "#C59A58",
            fontSize: { xs: "1.25rem", sm: "1.75rem", md: "2.5rem" },
            wordBreak: "break-word",
            flex: 1,
            minWidth: 0
          }}>
            SUPPLIER PAYMENTS
          </Typography>
          <Chip 
            label={`Supplier: ${supplier.SupplierCode}`} 
            sx={{ 
              bgcolor: "#C59A58", 
              color: "#000", 
              fontWeight: 600,
              fontSize: { xs: "0.7rem", sm: "0.85rem", md: "1rem" },
              px: { xs: 1, sm: 1.5, md: 2 },
              py: { xs: 1.5, sm: 2, md: 2.5 },
              height: { xs: 28, sm: 36, md: 40 },
              minWidth: "fit-content"
            }} 
          />
        </Box>

        {/* BACK BUTTON */}
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/admin/suppliers")}
          sx={{ 
            mb: 2,
            borderColor: "#C59A58",
            color: "#C59A58",
            "&:hover": { borderColor: "#b68649", bgcolor: "rgba(197, 154, 88, 0.04)" },
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
            px: { xs: 1.5, sm: 2, md: 3 },
            py: { xs: 0.75, sm: 1 }
          }}
        >
          Back to Suppliers
        </Button>

        {/* SUPPLIER INFO CARD */}
        <Paper elevation={6} sx={{ 
          p: { xs: 1.5, sm: 2, md: 3 }, 
          mb: 3, 
          borderRadius: 2, 
          background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
          width: "100%"
        }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 600, 
              color: "#C59A58",
              fontSize: { xs: "0.95rem", sm: "1.1rem", md: "1.25rem" }
            }}>
              Supplier Information
            </Typography>
          </Box>
          
          <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="center">
            <Grid item xs={12} md={3} sx={{ display: "flex", justifyContent: "center" }}>
              <Avatar
                sx={{
                  width: { xs: 80, sm: 90, md: 100 },
                  height: { xs: 80, sm: 90, md: 100 },
                  bgcolor: avatarColor,
                  fontSize: { xs: "1.8rem", sm: "2rem", md: "2.5rem" },
                  fontWeight: 600,
                }}
              >
                {initials}
              </Avatar>
            </Grid>

            <Grid item xs={12} md={5}>
              <Typography variant="body2" sx={{ mb: 0.4, color: "#C59A58", fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.875rem" } }}>
                Supplier Code
              </Typography>
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                color: "#fff",
                fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.25rem" },
                mb: 2
              }}>
                {supplier.SupplierCode}
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 0.4, color: "#C59A58", fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.875rem" } }}>
                Opening Balance
              </Typography>
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                color: "#fff",
                fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.25rem" }
              }}>
                ₹{openingBalance.toLocaleString('en-IN')}
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="body2" sx={{ mb: 0.4, color: "#C59A58", fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.875rem" }, textAlign: "center" }}>
                Current Amount Owed
              </Typography>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 700, 
                  textAlign: "center",
                  color: isOwed ? "#d32f2f" : "#4caf50",
                  fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" }
                }}
              >
                ₹{Math.abs(currentBalance).toLocaleString('en-IN')}
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  textAlign: "center", 
                  mt: 0.5,
                  color: isOwed ? "#d32f2f" : "#4caf50",
                  fontWeight: 600,
                  fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" }
                }}
              >
                {isOwed ? "(Amount Due)" : "(Paid Up)"}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* ACTION BUTTONS */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, mb: 2, flexDirection: { xs: "column", sm: "row" }, gap: 1.5 }}>
          <Typography variant="h5" sx={{ 
            fontWeight: 700,
            color: "#C59A58",
            fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.5rem" }
          }}>
            Payment Management
          </Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", width: { xs: "100%", sm: "auto" } }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingPayment(null);
                setPaymentForm({
                  amount: "",
                  paymentMethod: "Bank Transfer",
                  paymentStatus: "Completed",
                  paymentDate: new Date().toISOString().split("T")[0],
                  notes: "",
                });
                setOpenPaymentDialog(true);
              }}
              sx={{ 
                bgcolor: "#C59A58", 
                "&:hover": { bgcolor: "#b68649" },
                fontWeight: 600,
                color: "#000",
                fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.875rem" },
                px: { xs: 1.5, sm: 2, md: 3 },
                py: { xs: 0.6, sm: 0.8 },
                flex: { xs: 1, sm: "none" }
              }}
            >
              Record Payment
            </Button>
            
            <Button
              variant="contained"
              startIcon={<AccountBalanceIcon />}
              onClick={() => {
                setBalanceForm({
                  amount: openingBalance > 0 ? openingBalance.toString() : "",
                  effectiveDate: new Date().toISOString().split("T")[0],
                  notes: "",
                });
                setOpenBalanceDialog(true);
              }}
              sx={{ 
                bgcolor: "#ff9800", 
                "&:hover": { bgcolor: "#f57c00" },
                fontWeight: 600,
                fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.875rem" },
                px: { xs: 1.5, sm: 2, md: 3 },
                py: { xs: 0.6, sm: 0.8 },
                flex: { xs: 1, sm: "none" }
              }}
            >
              {openingBalance > 0 ? "Update" : "Set"} Opening Balance
            </Button>
          </Box>
        </Box>

        {/* TABS */}
        <Paper elevation={6} sx={{ 
          borderRadius: 2, 
          bgcolor: "#1a1a1a",
          border: "1px solid #333",
          boxShadow: "0 8px 32px rgba(0,0,0,0.3)"
        }}>
          <Tabs 
            value={tabValue} 
            onChange={(e, newValue) => setTabValue(newValue)}
            sx={{ 
              borderBottom: 1, 
              borderColor: '#333',
              '& .MuiTab-root': { 
                fontWeight: 600,
                color: "#888",
                fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.875rem" }
              },
              '& .Mui-selected': { color: "#C59A58 !important" },
              '& .MuiTabs-indicator': { backgroundColor: "#C59A58" }
            }}
          >
            <Tab icon={<HistoryIcon />} label="Payment History" />
            <Tab icon={<ShoppingCartIcon />} label="Purchase History" />
          </Tabs>

          {/* PAYMENT HISTORY TAB */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ overflowX: "auto", width: "100%" }}>
              <TableContainer sx={{ 
                borderRadius: 2,
                minWidth: { xs: "100%", sm: 500, md: 600 }
              }}>
                <Table
                  sx={{
                    borderCollapse: "separate",
                    borderSpacing: "0 8px",
                  }}
                  aria-label="payment history table"
                >
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#C59A58" }}>
                      <TableCell sx={{ 
                        color: "#000", 
                        fontWeight: 700, 
                        border: "none",
                        fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.875rem" },
                        p: { xs: 0.75, sm: 1, md: 1.5 },
                        whiteSpace: "nowrap"
                      }}>
                        Date
                      </TableCell>
                      <TableCell sx={{ 
                        color: "#000", 
                        fontWeight: 700, 
                        border: "none",
                        fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.875rem" },
                        p: { xs: 0.75, sm: 1, md: 1.5 },
                        whiteSpace: "nowrap"
                      }}>
                        Amount
                      </TableCell>
                      <TableCell sx={{ 
                        color: "#000", 
                        fontWeight: 700, 
                        border: "none",
                        fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.875rem" },
                        p: { xs: 0.75, sm: 1, md: 1.5 },
                        whiteSpace: "nowrap",
                        display: { xs: "none", sm: "table-cell" }
                      }}>
                        Method
                      </TableCell>
                      <TableCell sx={{ 
                        color: "#000", 
                        fontWeight: 700, 
                        border: "none",
                        fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.875rem" },
                        p: { xs: 0.75, sm: 1, md: 1.5 },
                        whiteSpace: "nowrap"
                      }}>
                        Status
                      </TableCell>
                      <TableCell sx={{ 
                        color: "#000", 
                        fontWeight: 700, 
                        border: "none",
                        fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.875rem" },
                        p: { xs: 0.75, sm: 1, md: 1.5 },
                        whiteSpace: "nowrap",
                        display: { xs: "none", md: "table-cell" }
                      }}>
                        Notes
                      </TableCell>
                      <TableCell align="center" sx={{ 
                        color: "#000", 
                        fontWeight: 700, 
                        border: "none",
                        fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.875rem" },
                        p: { xs: 0.75, sm: 1, md: 1.5 },
                        whiteSpace: "nowrap",
                        width: "60px"
                      }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payments.length > 0 ? (
                      payments.map((payment, index) => (
                        <TableRow
                          key={payment.PaymentId}
                          sx={{
                            bgcolor: index % 2 === 0 ? "#2a2a2a" : "#333",
                            borderRadius: 1,
                            "&:hover": { boxShadow: 1 },
                            "& td": { 
                              border: "none", 
                              verticalAlign: "middle",
                              fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.875rem" },
                              p: { xs: 0.6, sm: 0.8, md: 1.5 }
                            },
                          }}
                        >
                          <TableCell sx={{ fontWeight: 600, color: "#C59A58", whiteSpace: "nowrap" }}>
                            {new Date(payment.PaymentDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#4caf50", whiteSpace: "nowrap" }}>
                            ₹{Math.abs(payment.Amount).toLocaleString('en-IN')}
                          </TableCell>
                          <TableCell sx={{ color: "#fff", display: { xs: "none", sm: "table-cell" }, whiteSpace: "nowrap" }}>
                            {payment.PaymentMethod}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            <Chip
                              label={payment.PaymentStatus}
                              size="small"
                              sx={{
                                bgcolor: payment.PaymentStatus === "Completed" ? "#2e7d32" : 
                                         payment.PaymentStatus === "Pending" ? "#ed6c02" : "#1976d2",
                                color: "white",
                                fontWeight: 600,
                                fontSize: { xs: "0.6rem", sm: "0.7rem" }
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ 
                            maxWidth: 150, 
                            overflow: "hidden", 
                            textOverflow: "ellipsis", 
                            color: "#fff",
                            display: { xs: "none", md: "table-cell" },
                            whiteSpace: "nowrap"
                          }}>
                            {payment.Notes || "-"}
                          </TableCell>
                          <TableCell align="center" sx={{ width: "60px" }}>
                            <IconButton
                              onClick={(e) => handleMenuClick(e, payment)}
                              sx={{ 
                                color: "#C59A58",
                                p: { xs: 0.5, sm: 0.8 }
                              }}
                              size="small"
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 2, border: "none" }}>
                          <Typography variant="body1" sx={{ 
                            color: "#888",
                            fontSize: { xs: "0.7rem", sm: "0.8rem", md: "1rem" }
                          }}>
                            No payment history yet. Record your first payment!
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </TabPanel>

          {/* PURCHASE HISTORY TAB */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ overflowX: "auto", width: "100%" }}>
              <TableContainer sx={{ 
                borderRadius: 2,
                minWidth: { xs: "100%", sm: 500, md: 700 }
              }}>
                <Table
                  sx={{
                    borderCollapse: "separate",
                    borderSpacing: "0 8px",
                  }}
                  aria-label="purchase history table"
                >
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#C59A58" }}>
                      <TableCell sx={{ 
                        color: "#000", 
                        fontWeight: 700, 
                        border: "none",
                        fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.875rem" },
                        p: { xs: 0.75, sm: 1, md: 1.5 },
                        whiteSpace: "nowrap"
                      }}>
                        Date
                      </TableCell>
                      <TableCell sx={{ 
                        color: "#000", 
                        fontWeight: 700, 
                        border: "none",
                        fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.875rem" },
                        p: { xs: 0.75, sm: 1, md: 1.5 },
                        whiteSpace: "nowrap"
                      }}>
                        Type
                      </TableCell>
                      <TableCell sx={{ 
                        color: "#000", 
                        fontWeight: 700, 
                        border: "none",
                        fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.875rem" },
                        p: { xs: 0.75, sm: 1, md: 1.5 },
                        whiteSpace: "nowrap",
                        display: { xs: "none", sm: "table-cell" }
                      }}>
                        Item
                      </TableCell>
                      <TableCell sx={{ 
                        color: "#000", 
                        fontWeight: 700, 
                        border: "none",
                        fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.875rem" },
                        p: { xs: 0.75, sm: 1, md: 1.5 },
                        whiteSpace: "nowrap",
                        display: { xs: "none", md: "table-cell" }
                      }}>
                        Quantity
                      </TableCell>
                      <TableCell sx={{ 
                        color: "#000", 
                        fontWeight: 700, 
                        border: "none",
                        fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.875rem" },
                        p: { xs: 0.75, sm: 1, md: 1.5 },
                        whiteSpace: "nowrap"
                      }}>
                        Total
                      </TableCell>
                      <TableCell align="center" sx={{ 
                        color: "#000", 
                        fontWeight: 700, 
                        border: "none",
                        fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.875rem" },
                        p: { xs: 0.75, sm: 1, md: 1.5 },
                        whiteSpace: "nowrap",
                        width: "80px"
                      }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {purchaseHistory.length > 0 ? (
                      purchaseHistory.map((purchase, index) => (
                        <TableRow
                          key={index}
                          sx={{
                            bgcolor: index % 2 === 0 ? "#2a2a2a" : "#333",
                            borderRadius: 1,
                            "&:hover": { boxShadow: 1 },
                            "& td": { 
                              border: "none", 
                              verticalAlign: "middle",
                              fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.875rem" },
                              p: { xs: 0.6, sm: 0.8, md: 1.5 }
                            },
                          }}
                        >
                          <TableCell sx={{ fontWeight: 600, color: "#C59A58", whiteSpace: "nowrap" }}>
                            {new Date(purchase.purchaseDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            <Chip
                              label={purchase.purchaseType}
                              size="small"
                              sx={{
                                bgcolor: purchase.purchaseType === "Product" ? "#1976d2" : "#9c27b0",
                                color: "white",
                                fontWeight: 600,
                                fontSize: { xs: "0.6rem", sm: "0.7rem" }
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ 
                            color: "#fff", 
                            display: { xs: "none", sm: "table-cell" }, 
                            whiteSpace: "nowrap",
                            maxWidth: 120,
                            overflow: "hidden",
                            textOverflow: "ellipsis"
                          }}>
                            {purchase.itemName}
                          </TableCell>
                          <TableCell sx={{ color: "#fff", display: { xs: "none", md: "table-cell" }, whiteSpace: "nowrap" }}>
                            {purchase.quantity}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#d32f2f", whiteSpace: "nowrap" }}>
                            ₹{purchase.totalCost.toLocaleString('en-IN')}
                          </TableCell>
                          <TableCell align="center" sx={{ width: "80px" }}>
                            <IconButton
                              onClick={() => handleViewPurchaseDetails(purchase)}
                              sx={{ 
                                color: "#C59A58",
                                p: { xs: 0.5, sm: 0.8 }
                              }}
                              size="small"
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 2, border: "none" }}>
                          <Typography variant="body1" sx={{ 
                            color: "#888",
                            fontSize: { xs: "0.7rem", sm: "0.8rem", md: "1rem" }
                          }}>
                            No purchase history found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </TabPanel>
        </Paper>
      </Box>

      {/* ADD/EDIT PAYMENT DIALOG */}
      <Dialog 
        open={openPaymentDialog} 
        onClose={() => {
          setOpenPaymentDialog(false);
          setEditingPayment(null);
        }} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "#1a1a1a",
            border: "1px solid #333",
            borderRadius: 2,
            mx: { xs: 1, sm: 0 }
          }
        }}
      >
        <DialogTitle sx={{ color: "#C59A58", fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" }, p: { xs: 1.5, sm: 2 } }}>
          {editingPayment ? "Edit Payment" : "Record Payment"}
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              <Grid item xs={12}>
                <TextField
                  label="Payment Amount (₹)"
                  type="number"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  fullWidth
                  required
                  slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#444' },
                      '&:hover fieldset': { borderColor: '#C59A58' },
                      '&.Mui-focused fieldset': { borderColor: '#C59A58' },
                      bgcolor: '#2a2a2a',
                      color: '#fff'
                    },
                    '& .MuiInputLabel-root': { color: '#888' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#C59A58' }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Payment Method"
                  value={paymentForm.paymentMethod}
                  onChange={(e) => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value })}
                  fullWidth
                  slotProps={{ 
                    select: { 
                      native: true,
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#444' },
                      '&:hover fieldset': { borderColor: '#C59A58' },
                      '&.Mui-focused fieldset': { borderColor: '#C59A58' },
                      bgcolor: '#2a2a2a',
                      color: '#fff'
                    },
                    '& .MuiInputLabel-root': { color: '#888' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#C59A58' }
                  }}
                >
                  <option value="Cash">Cash</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Cheque">Cheque</option>
                  <option value="UPI">UPI</option>
                  <option value="Card">Card</option>
                  <option value="Other">Other</option>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Payment Status"
                  value={paymentForm.paymentStatus}
                  onChange={(e) => setPaymentForm({ ...paymentForm, paymentStatus: e.target.value })}
                  fullWidth
                  slotProps={{ 
                    select: { 
                      native: true,
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#444' },
                      '&:hover fieldset': { borderColor: '#C59A58' },
                      '&.Mui-focused fieldset': { borderColor: '#C59A58' },
                      bgcolor: '#2a2a2a',
                      color: '#fff'
                    },
                    '& .MuiInputLabel-root': { color: '#888' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#C59A58' }
                  }}
                >
                  <option value="Completed">Completed</option>
                  <option value="Pending">Pending</option>
                  <option value="Failed">Failed</option>
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Payment Date"
                  type="date"
                  value={paymentForm.paymentDate}
                  onChange={(e) => setPaymentForm({ ...paymentForm, paymentDate: e.target.value })}
                  fullWidth
                  required
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#444' },
                      '&:hover fieldset': { borderColor: '#C59A58' },
                      '&.Mui-focused fieldset': { borderColor: '#C59A58' },
                      bgcolor: '#2a2a2a',
                      color: '#fff'
                    },
                    '& .MuiInputLabel-root': { color: '#888' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#C59A58' }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Notes"
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Add any additional notes about this payment..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#444' },
                      '&:hover fieldset': { borderColor: '#C59A58' },
                      '&.Mui-focused fieldset': { borderColor: '#C59A58' },
                      bgcolor: '#2a2a2a',
                      color: '#fff'
                    },
                    '& .MuiInputLabel-root': { color: '#888' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#C59A58' }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Button 
            onClick={() => {
              setOpenPaymentDialog(false);
              setEditingPayment(null);
            }}
            sx={{ color: '#888' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddPayment} 
            variant="contained" 
            sx={{ 
              bgcolor: "#C59A58", 
              "&:hover": { bgcolor: "#b68649" },
              color: "#000",
              fontWeight: 600
            }}
          >
            {editingPayment ? "Update Payment" : "Record Payment"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* OPENING BALANCE DIALOG */}
      <Dialog 
        open={openBalanceDialog} 
        onClose={() => setOpenBalanceDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "#1a1a1a",
            border: "1px solid #333",
            borderRadius: 2,
            mx: { xs: 1, sm: 0 }
          }
        }}
      >
        <DialogTitle sx={{ color: "#ff9800", fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" }, p: { xs: 1.5, sm: 2 } }}>
          {openingBalance > 0 ? "Update" : "Set"} Opening Balance
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              <Grid item xs={12}>
                <TextField
                  label="Opening Balance Amount (₹)"
                  type="number"
                  value={balanceForm.amount}
                  onChange={(e) => setBalanceForm({ ...balanceForm, amount: e.target.value })}
                  fullWidth
                  required
                  slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#444' },
                      '&:hover fieldset': { borderColor: '#ff9800' },
                      '&.Mui-focused fieldset': { borderColor: '#ff9800' },
                      bgcolor: '#2a2a2a',
                      color: '#fff'
                    },
                    '& .MuiInputLabel-root': { color: '#888' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#ff9800' }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Effective Date"
                  type="date"
                  value={balanceForm.effectiveDate}
                  onChange={(e) => setBalanceForm({ ...balanceForm, effectiveDate: e.target.value })}
                  fullWidth
                  required
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#444' },
                      '&:hover fieldset': { borderColor: '#ff9800' },
                      '&.Mui-focused fieldset': { borderColor: '#ff9800' },
                      bgcolor: '#2a2a2a',
                      color: '#fff'
                    },
                    '& .MuiInputLabel-root': { color: '#888' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#ff9800' }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Notes"
                  value={balanceForm.notes}
                  onChange={(e) => setBalanceForm({ ...balanceForm, notes: e.target.value })}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Add notes about this opening balance..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#444' },
                      '&:hover fieldset': { borderColor: '#ff9800' },
                      '&.Mui-focused fieldset': { borderColor: '#ff9800' },
                      bgcolor: '#2a2a2a',
                      color: '#fff'
                    },
                    '& .MuiInputLabel-root': { color: '#888' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#ff9800' }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Button onClick={() => setOpenBalanceDialog(false)} sx={{ color: "#888" }}>
            Cancel
          </Button>
          <Button 
            onClick={handleOpeningBalance} 
            variant="contained" 
            sx={{ bgcolor: "#ff9800", "&:hover": { bgcolor: "#f57c00" }, fontWeight: 600 }}
          >
            {openingBalance > 0 ? "Update" : "Set"} Balance
          </Button>
        </DialogActions>
      </Dialog>

      {/* DELETE CONFIRMATION DIALOG */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: "#1a1a1a",
            border: "1px solid #333",
            borderRadius: 2
          }
        }}
      >
        <DialogTitle sx={{ color: "#C59A58" }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#fff" }}>
            Are you sure you want to delete this payment? This will recalculate the supplier balance.
          </Typography>
          {paymentToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: "#2a2a2a", borderRadius: 1, border: "1px solid #444" }}>
              <Typography variant="body2" sx={{ color: "#fff" }}>
                <strong style={{ color: "#C59A58" }}>Amount:</strong> ₹{Math.abs(paymentToDelete.Amount).toLocaleString('en-IN')}
              </Typography>
              <Typography variant="body2" sx={{ color: "#fff" }}>
                <strong style={{ color: "#C59A58" }}>Date:</strong> {new Date(paymentToDelete.PaymentDate).toLocaleDateString()}
              </Typography>
              {paymentToDelete.Notes && (
                <Typography variant="body2" sx={{ color: "#fff" }}>
                  <strong style={{ color: "#C59A58" }}>Notes:</strong> {paymentToDelete.Notes}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: "#888" }}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            bgcolor: "#2a2a2a",
            border: "1px solid #444",
            borderRadius: 1,
            minWidth: 120
          }
        }}
      >
        <MenuItem 
          onClick={() => handleEditPayment(selectedPayment)}
          sx={{ 
            color: "#fff",
            fontSize: "0.875rem",
            "&:hover": { bgcolor: "#444" }
          }}
        >
          <EditIcon sx={{ mr: 1, fontSize: "1rem", color: "#C59A58" }} />
          Edit
        </MenuItem>
        <MenuItem 
          onClick={() => handleDeleteClick(selectedPayment)}
          sx={{ 
            color: "#fff",
            fontSize: "0.875rem",
            "&:hover": { bgcolor: "#444" }
          }}
        >
          <DeleteIcon sx={{ mr: 1, fontSize: "1rem", color: "#f44336" }} />
          Delete
        </MenuItem>
      </Menu>

      {/* PURCHASE DETAILS DIALOG */}
      <Dialog
        open={purchaseDetailsOpen}
        onClose={handleClosePurchaseDetails}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "#1a1a1a",
            border: "1px solid #333",
            borderRadius: 2,
            mx: { xs: 1, sm: 0 }
          }
        }}
      >
        <DialogTitle sx={{ 
          color: "#C59A58", 
          fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" }, 
          p: { xs: 1.5, sm: 2 },
          borderBottom: "1px solid #333"
        }}>
          Purchase Transaction Details
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          {selectedPurchase && (
            <Box sx={{ mt: 1 }}>
              {/* Transaction Header */}
              <Box sx={{ 
                mb: 3, 
                p: 2, 
                bgcolor: "#2a2a2a", 
                borderRadius: 2, 
                border: "1px solid #444" 
              }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#C59A58", fontSize: "0.8rem", mb: 0.5 }}>
                      Transaction Date
                    </Typography>
                    <Typography variant="h6" sx={{ color: "#fff", fontWeight: 600 }}>
                      {new Date(selectedPurchase.purchaseDate).toLocaleDateString('en-IN', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#C59A58", fontSize: "0.8rem", mb: 0.5 }}>
                      Transaction Type
                    </Typography>
                    <Chip
                      label={selectedPurchase.purchaseType}
                      sx={{
                        bgcolor: selectedPurchase.purchaseType === "Product" ? "#1976d2" : "#9c27b0",
                        color: "white",
                        fontWeight: 600,
                        fontSize: "0.9rem",
                        height: 32
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Item Details */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ color: "#C59A58", mb: 2, fontWeight: 600 }}>
                  Item Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ 
                      p: 2, 
                      bgcolor: "#2a2a2a", 
                      borderRadius: 1, 
                      border: "1px solid #444",
                      height: "100%"
                    }}>
                      <Typography variant="body2" sx={{ color: "#C59A58", fontSize: "0.8rem", mb: 1 }}>
                        Item Name
                      </Typography>
                      <Typography variant="body1" sx={{ color: "#fff", fontWeight: 500 }}>
                        {selectedPurchase.itemName}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ 
                      p: 2, 
                      bgcolor: "#2a2a2a", 
                      borderRadius: 1, 
                      border: "1px solid #444",
                      height: "100%"
                    }}>
                      <Typography variant="body2" sx={{ color: "#C59A58", fontSize: "0.8rem", mb: 1 }}>
                        Quantity Purchased
                      </Typography>
                      <Typography variant="body1" sx={{ color: "#fff", fontWeight: 500 }}>
                        {selectedPurchase.quantity}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Financial Details */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ color: "#C59A58", mb: 2, fontWeight: 600 }}>
                  Financial Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ 
                      p: 2, 
                      bgcolor: "#2a2a2a", 
                      borderRadius: 1, 
                      border: "1px solid #444",
                      textAlign: "center"
                    }}>
                      <Typography variant="body2" sx={{ color: "#C59A58", fontSize: "0.8rem", mb: 1 }}>
                        Unit Price
                      </Typography>
                      <Typography variant="h6" sx={{ color: "#4caf50", fontWeight: 600 }}>
                        ₹{selectedPurchase.unitPrice.toLocaleString('en-IN')}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ 
                      p: 2, 
                      bgcolor: "#2a2a2a", 
                      borderRadius: 1, 
                      border: "1px solid #444",
                      textAlign: "center"
                    }}>
                      <Typography variant="body2" sx={{ color: "#C59A58", fontSize: "0.8rem", mb: 1 }}>
                        Quantity
                      </Typography>
                      <Typography variant="h6" sx={{ color: "#fff", fontWeight: 600 }}>
                        {selectedPurchase.quantity}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ 
                      p: 2, 
                      bgcolor: "#2a2a2a", 
                      borderRadius: 1, 
                      border: "1px solid #C59A58",
                      textAlign: "center"
                    }}>
                      <Typography variant="body2" sx={{ color: "#C59A58", fontSize: "0.8rem", mb: 1 }}>
                        Total Cost
                      </Typography>
                      <Typography variant="h5" sx={{ color: "#d32f2f", fontWeight: 700 }}>
                        ₹{selectedPurchase.totalCost.toLocaleString('en-IN')}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Additional Notes */}
              {selectedPurchase.notes && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ color: "#C59A58", mb: 2, fontWeight: 600 }}>
                    Additional Notes
                  </Typography>
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: "#2a2a2a", 
                    borderRadius: 1, 
                    border: "1px solid #444"
                  }}>
                    <Typography variant="body1" sx={{ color: "#fff", lineHeight: 1.6 }}>
                      {selectedPurchase.notes}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Summary */}
              <Divider sx={{ my: 2, borderColor: "#444" }} />
              <Box sx={{ 
                p: 2, 
                bgcolor: "linear-gradient(135deg, #2a2a2a 0%, #333 100%)", 
                borderRadius: 1, 
                border: "1px solid #C59A58"
              }}>
                <Typography variant="body2" sx={{ color: "#C59A58", fontSize: "0.9rem", textAlign: "center" }}>
                  This transaction contributed <strong>₹{selectedPurchase.totalCost.toLocaleString('en-IN')}</strong> to the supplier balance
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, borderTop: "1px solid #333" }}>
          <Button 
            onClick={handleClosePurchaseDetails}
            variant="contained"
            sx={{ 
              bgcolor: "#C59A58", 
              "&:hover": { bgcolor: "#b68649" },
              color: "#000",
              fontWeight: 600,
              px: 3
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </AppLayout>
  );
}